/**
 * La consegna riconciliata dei verdetti.
 *
 * **Che cosa promette questo modulo.** Che una decisione presa non si perda per
 * strada senza che nessuno se ne accorga. Un verdetto perso non lascia traccia
 * — l'operatore lo ha dato, la pagina e' andata avanti — e la mancanza salta
 * fuori alla consegna, quando l'anomalia non c'e' e nessuno sa piu' dire se
 * qualcuno l'aveva guardata.
 *
 * **Tre regole, e nessuna e' un dettaglio di implementazione.**
 *
 * 1. *La chiave nasce con la decisione, non con l'invio.* E' cio' che rende
 *    sicuro il rinvio: il servizio riconosce il secondo tentativo come lo
 *    stesso verdetto. Se nascesse all'invio, due tentativi sarebbero due
 *    verdetti.
 * 2. *Il lavoro obsoleto non si applica da solo.* Un lease scaduto vuol dire
 *    che il candidato e' tornato in coda: scriverci sopra il verdetto di prima
 *    e' il doppione che i lease esistono per impedire. Ma sparire in silenzio
 *    sarebbe lavoro tolto all'operatore senza dirglielo — resta visibile, e
 *    sceglie lui.
 * 3. *La finestra d'annullamento e' quella del servente.* Un orologio locale
 *    indietro di un minuto ritirerebbe verdetti che il turno ha gia' dato per
 *    fatti.
 *
 * **Fuori linea si accumula quanto basta, non tutto il turno.** Oltre il numero
 * di candidati che una sessione puo' tenere in mano, cio' che si accumula non
 * e' nemmeno piu' applicabile: il lease sarebbe scaduto molto prima.
 */
import { ServiceError, newKey } from './api'
import {
  applyIntent,
  isRejection,
  isStaleWork,
  detectionIn,
  isCoverage,
  reconcileIntents,
  registerIntent,
  subjectOf,
} from './reviewProtocol'
import type {
  IntentKind,
  ReconciledIntent,
  RegisteredIntent,
  ReviewIntent,
} from './reviewProtocol'
import { adopt } from './reviewStore'
import type { ReviewStore } from './reviewStore'

/**
 * Quante decisioni non consegnate si accettano prima di dire basta.
 *
 * Il numero e' volutamente piccolo: non e' una coda per lavorare una giornata
 * scollegati, ma il margine per attraversare un guasto di rete tenendo in mano
 * il lavoro gia' riservato. Piu' in la' il lease sarebbe comunque scaduto, e si
 * accumulerebbero verdetti che nessuno potra' applicare.
 */
export const MAX_UNSENT = 20

/**
 * Dopo quanti tentativi una consegna smette di insistere *per ora*.
 *
 * Il verdetto non si perde: resta e riparte al richiamo successivo. Senza
 * questo limite `flush` non tornerebbe mai indietro con la rete giu' — il
 * tentativo che conta, quello di quando la rete torna, non partirebbe mai
 * perche' il precedente non ha finito.
 */
const ENOUGH_FOR_NOW = 6

/** Quanto aspettare prima di riprovare: raddoppia, ma non oltre mezzo minuto —
 *  oltre, l'operatore chiuderebbe la pagina credendo tutto perduto. */
export function waitFor(attempts: number): number {
  return Math.min(1000 * 2 ** Math.max(0, attempts - 1), 30_000)
}

/**
 * La decisione che non e' stata presa in carico, e perche'.
 *
 * Un'eccezione qualsiasi qui non basterebbe: chi chiama deve poter distinguere
 * «rimettiti in linea» da «questo navigatore non ha un deposito», perche' le
 * due cose si risolvono in modi opposti. E deve poterlo fare senza leggere una
 * frase, che domani cambia.
 */
export class NotTaken extends Error {
  readonly code: 'outbox_full' | 'storage_unavailable'

  constructor(code: NotTaken['code'], message: string) {
    super(message)
    this.name = 'NotTaken'
    this.code = code
  }
}

/**
 * Un esito che vuole una persona.
 *
 * Respinto o obsoleto, l'intento lascia la coda: il contatore delle decisioni
 * da consegnare scende, e sullo schermo quello e' il segno che tutto e'
 * passato. Senza questo annuncio sarebbe la bugia peggiore che la pagina possa
 * raccontare — l'operatore ha gia' voltato pagina, e la mancanza si vedra' alla
 * consegna, quando l'anomalia non c'e' piu'.
 */
export interface Settled {
  intent: ReviewIntent
  state: 'rejected' | 'stale'
  code: string | null
  message: string
}

/** Una decisione appena presa, prima che il deposito le dia una chiave. */
export interface Seed {
  sessionId: string
  /** Nullo per le tre dichiarazioni di copertura: quelle hanno un fotogramma o
   *  un elemento come soggetto. */
  detectionId: string | null
  photoId?: string | null
  elementId?: string | null
  leaseToken: string | null
  reviewEpoch: number
  expectedCandidateVersion: number
  kind: IntentKind
  payload: Record<string, unknown>
  originalKey?: string | null
}

/** I tre gesti verso il servizio. Iniettabili perche' le prove non debbano
 *  parlare con nessuno. */
export interface Protocol {
  register(intent: ReviewIntent): Promise<RegisteredIntent>
  apply(sessionId: string, key: string): Promise<RegisteredIntent>
  reconcile(sessionId: string, keys: string[]): Promise<ReconciledIntent[]>
}

/**
 * Che cosa succede quando si annulla.
 *
 * Cinque esiti e non due, perche' sono cinque situazioni che l'operatore vede
 * diverse: la decisione non ancora partita si toglie e basta; quella scritta si
 * ritira con una riga in piu'; quella vecchia di piu' di cinque secondi non si
 * ritira — si riprende il candidato; quella che il servizio non ha registrato
 * non ha niente da ritirare; e quella di cui non si riesce a sapere l'esito
 * resta com'e', perche' ritirare alla cieca vorrebbe dire ritirare un verdetto
 * che potrebbe non esistere.
 *
 * Non c'e' piu' un esito «in volo»: una decisione registrata ma senza risposta
 * si porta a termine — l'applicazione e' idempotente per chiave — e poi si
 * ritira. Rispondere «aspetta» lasciava un tasto che non risponde proprio nei
 * cinque secondi in cui serve.
 */
export type Undone =
  | { kind: 'removed' }
  | { kind: 'reversal'; intent: ReviewIntent }
  | { kind: 'too_late' }
  | { kind: 'nothing_written'; intent: ReviewIntent }
  | { kind: 'unreachable' }
  | { kind: 'unknown' }

export interface SyncOptions {
  store: ReviewStore
  protocol?: Protocol
  /** Iniettabile perche' le prove non debbano aspettare davvero. */
  sleep?: (ms: number) => Promise<void>
  /** L'orologio locale. Serve per sapere *quando chiedere*, mai per decidere
   *  se una finestra del servente e' ancora aperta. */
  now?: () => number
  storage?: Storage | null
  onChange?: (waiting: number) => void
  /** Che cosa fare di cio' che non e' passato. Una volta sola per intento: un
   *  avviso che ricompare a ogni consegna seppellisce quelli nuovi. */
  onSettled?: (settled: Settled) => void
}

export interface ReviewSync {
  /** Adotta cio' che era rimasto nel vecchio deposito. Una volta sola. */
  ready(): Promise<ReviewIntent[]>
  /** Prende in carico la decisione e restituisce subito. */
  add(seed: Seed): Promise<ReviewIntent>
  /** Consegna finche' puo'. Sicuro da chiamare piu' volte. */
  flush(): Promise<void>
  /** Chiede al servizio che fine hanno fatto le proprie chiavi. Non applica
   *  niente. */
  reconcile(sessionId: string): Promise<void>
  /** Annulla, per quanto lo stato lo consenta. */
  undo(key: string): Promise<Undone>
  /** Annulla l'ultima decisione presa su un candidato, cercandola nel
   *  deposito. La mappa candidato→chiave in memoria muore col ricaricamento
   *  della pagina, e con lei la possibilita' di ritirare.
   *
   *  Senza candidato: l'ultima decisione della sessione, qualunque sia. E'
   *  il caso della pagina appena riaperta, che non sa piu' su che cosa si
   *  era deciso ma ha ancora i cinque secondi per rimediare. */
  undoLatest(sessionId: string, detectionId: string | null): Promise<Undone>
  /** Cio' che deve ancora partire. */
  waiting(): Promise<ReviewIntent[]>
  /** Cio' che vuole una persona: respinto o obsoleto. */
  attention(): Promise<ReviewIntent[]>
  /**
   * L'ultima decisione scritta su ogni candidato.
   *
   * Serve a chi deve dire, candidato per candidato, che fine ha fatto il
   * verdetto. Sta qui e non nella schermata perche' e' il deposito a saperlo:
   * una copia tenuta di la' andrebbe riallineata a ogni cambiamento, e il
   * giorno in cui divergono la differenza si vede come una riga della coda che
   * dice il contrario di quello che e' successo.
   */
  latest(): Promise<Map<string, ReviewIntent>>
  /**
   * Dimentica i rifiuti su questi candidati: e' arrivato un lease nuovo.
   *
   * Un rifiuto — `rejected` o `stale` — dice che *quel* tentativo non e'
   * passato; non dice niente del candidato. Quando il servizio consegna un
   * lease nuovo sullo stesso candidato, il tentativo vecchio e' storia:
   * lasciarlo come ultima parola sul soggetto farebbe dire alla schermata
   * «tornato in coda, va ripreso» su un candidato che si ha in mano adesso.
   * Non c'e' niente da ritirare dal servizio: un rifiuto non ha scritto nulla.
   */
  release(detectionIds: string[], exceptSession?: string): Promise<string[]>
}

const UNSENT: ReviewIntent['state'][] = ['local', 'decision_pending']
const OPEN: ReviewIntent['state'][] = ['rejected', 'stale']

/** Lo stato del servizio, riportato senza reinterpretarlo. Gli stessi nomi di
 *  la': tradurli qui vorrebbe dire che due vocabolari devono restare allineati
 *  a mano, e il giorno in cui divergono la differenza si vede come un intento
 *  che resta fermo senza motivo. */
function mapped(state: string): ReviewIntent['state'] {
  if (state === 'applied') return 'applied'
  if (state === 'rejected') return 'rejected'
  if (state === 'stale') return 'stale'
  return 'decision_pending'
}

export function reviewSync(options: SyncOptions): ReviewSync {
  const { store } = options
  const protocol: Protocol = options.protocol ?? {
    register: registerIntent,
    apply: applyIntent,
    reconcile: reconcileIntents,
  }
  const sleep = options.sleep ?? ((ms: number) => new Promise((done) => setTimeout(done, ms)))
  const now = options.now ?? (() => Date.now())
  const storage = options.storage === undefined ? window.localStorage : options.storage
  let inFlight: Promise<void> | null = null
  // Di quali esiti si e' gia' parlato. Ripeterli a ogni consegna li
  // trasformerebbe in rumore, e dopo dieci ripetizioni l'operatore smette di
  // leggerli — proprio quando ne arriva uno che conta.
  const told = new Set<string>()
  // L'istante dell'ultima decisione. Due decisioni prese nello stesso
  // millisecondo avrebbero lo stesso istante, e l'ordine fra le due
  // dipenderebbe da chiavi casuali: un ritiro potrebbe partire prima del
  // verdetto che annulla, e il servizio lo rifiuterebbe con «non c'e' nulla da
  // annullare».
  let last = 0

  function stamp(): string {
    last = Math.max(now(), last + 1)
    return new Date(last).toISOString()
  }

  async function announce(): Promise<void> {
    if (!options.onChange) return
    options.onChange((await waiting()).length)
  }

  /**
   * Una lettura dal deposito, con un nome per quando non risponde.
   *
   * Un deposito illeggibile non e' un dettaglio tecnico: e' il momento in cui
   * la pagina non sa piu' che cosa ha deciso l'operatore. Chi chiama deve
   * poterlo distinguere da «il limite e' pieno» senza leggere una frase, e
   * deve poterlo **dire**, non ingoiare.
   */
  async function read<T>(from: () => Promise<T>): Promise<T> {
    try {
      return await from()
    } catch {
      throw new NotTaken(
        'storage_unavailable',
        "Il deposito delle decisioni non risponde: la decisione non e' stata presa in carico.",
      )
    }
  }

  /**
   * Una scrittura sul deposito, con lo stesso esito parlante delle letture.
   *
   * Quando il deposito non scrive, «errore generico» non basta: chi chiama
   * deve poter dire subito che la decisione non e' stata messa al sicuro.
   */
  async function write<T>(into: () => Promise<T>): Promise<T> {
    try {
      return await into()
    } catch {
      throw new NotTaken(
        'storage_unavailable',
        "Il deposito delle decisioni non risponde: la decisione non e' stata presa in carico.",
      )
    }
  }

  /** Dice cio' che non e' passato, una volta sola. */
  function tell(intent: ReviewIntent): void {
    if (intent.state !== 'rejected' && intent.state !== 'stale') return
    if (told.has(intent.idempotencyKey)) return
    told.add(intent.idempotencyKey)
    options.onSettled?.({
      intent,
      state: intent.state,
      code: intent.rejectionCode ?? null,
      message:
        intent.note ??
        said(intent.state, intent.rejectionCode ?? null) ??
        "La decisione non e' stata registrata.",
    })
  }

  /** Scrive nel deposito e, se e' un esito che vuole una persona, lo dice.
   *  Le due cose stanno insieme perche' separarle e' esattamente il difetto
   *  che questo modulo aveva: lo stato scritto e nessuno avvisato. */
  async function keep(intent: ReviewIntent): Promise<ReviewIntent> {
    await write(() => store.put(intent))
    tell(intent)
    return intent
  }

  /** Cio' che questo modulo puo' consegnare.
   *
   * Senza sessione non si consegna: le righe della vecchia strada diretta e
   * quelle adottate dal deposito precedente convivono nello stesso deposito, e
   * spedirle per questa strada vorrebbe dire dichiarare un lease che non
   * esiste. Restano visibili — `attention` le mostra — ma non partono di qui. */
  async function waiting(): Promise<ReviewIntent[]> {
    return (await read(() => store.all())).filter(
      (one) => UNSENT.includes(one.state) && one.sessionId !== '',
    )
  }

  /** Assorbe la risposta del servizio senza aggiungerci interpretazioni. */
  function absorb(intent: ReviewIntent, answer: RegisteredIntent): ReviewIntent {
    const state = mapped(answer.state)
    return {
      ...intent,
      state,
      undoDeadline: answer.undo_deadline,
      reviewId: answer.review_id,
      candidateVersion: answer.candidate_version,
      rejectionCode: answer.rejection_code ?? intent.rejectionCode ?? null,
      note: said(state, answer.rejection_code, intent.kind) ?? intent.note ?? null,
      attempts: 0,
    }
  }

  async function step(intent: ReviewIntent): Promise<void> {
    const answer =
      intent.state === 'local'
        ? await protocol.register(intent)
        : await protocol.apply(intent.sessionId, intent.idempotencyKey)
    // Non tutti i «no» arrivano come errore: la risposta puo' portare dentro
    // uno stato respinto, ed e' un esito da dire come gli altri.
    await keep(absorb(intent, answer))
  }

  /** Come resta scritta una decisione che il servizio ha rifiutato. */
  function landed(intent: ReviewIntent, error: unknown): ReviewIntent {
    return {
      ...intent,
      state: isStaleWork(error) ? 'stale' : 'rejected',
      rejectionCode: error instanceof ServiceError ? error.code : null,
      note: message(error),
    }
  }

  /** Che cosa fare del fallimento. `true` se conviene riprovare subito. */
  async function stumbled(intent: ReviewIntent, error: unknown): Promise<boolean> {
    // Il lavoro obsoleto non si applica e non si ripete: il candidato e'
    // tornato in coda, e chi lo riprende decide di nuovo. Un rifiuto ripetuto
    // all'infinito non guarisce: blocca la coda e nasconde che il verdetto non
    // e' stato registrato. In entrambi i casi resta scritto e si dice.
    if (isStaleWork(error) || isRejection(error)) {
      await keep(landed(intent, error))
      return false
    }
    const attempts = (intent.attempts ?? 0) + 1
    await write(() => store.put({ ...intent, attempts }))
    if (attempts >= ENOUGH_FOR_NOW) return false
    await sleep(waitFor(attempts))
    return true
  }

  /**
   * Cio' che il servizio potrebbe gia' aver scritto.
   *
   * Registrato, oppure partito almeno una volta senza che se ne sia vista la
   * risposta. Sono le righe di cui si deve chiedere prima di riscrivere.
   */
  function risky(one: ReviewIntent): boolean {
    return one.state === 'decision_pending' || (one.attempts ?? 0) > 0
  }

  /**
   * Chiedere prima di scrivere.
   *
   * La chiave rende innocuo il rinvio, quindi non e' per evitare doppioni: e'
   * perche' un `rejected` o uno `stale` che il servizio ha gia' deciso si
   * scopre in un viaggio invece che dopo sei tentativi falliti, e nel
   * frattempo l'operatore ha deciso altri venti candidati sullo stesso
   * equivoco.
   */
  async function askFirst(): Promise<void> {
    const lost = (await waiting()).filter(risky)
    for (const sessionId of new Set(lost.map((one) => one.sessionId))) {
      try {
        await reconcile(sessionId)
      } catch {
        // Non si e' potuto chiedere. Si consegna lo stesso: rinunciare
        // lascerebbe fermo il lavoro proprio quando la rete e' instabile,
        // cioe' quando serve di piu'. La chiave protegge dal doppione.
      }
    }
  }

  async function reconcile(sessionId: string): Promise<void> {
    const open = (await waiting()).filter((one) => one.sessionId === sessionId)
    if (open.length === 0) return
    const answers = await protocol.reconcile(
      sessionId,
      open.map((one) => one.idempotencyKey),
    )
    const known = new Map(open.map((one) => [one.idempotencyKey, one]))
    for (const answer of answers) {
      const intent = known.get(answer.idempotency_key)
      if (!intent) continue
      await keep(settled(intent, answer))
    }
    await announce()
  }

  async function drain(): Promise<void> {
    for (;;) {
      const queue = await waiting()
      const item = queue[0]
      if (!item) return
      try {
        await step(item)
      } catch (error: unknown) {
        if (!(await stumbled(item, error))) {
          // O e' finita male per sempre, o si e' insistito abbastanza per ora.
          // Nel secondo caso la coda e' ancora piena e questo `flush` deve
          // tornare indietro, altrimenti il prossimo non partirebbe mai.
          const after = await read(() => store.get(item.idempotencyKey))
          if (after && UNSENT.includes(after.state)) return
        }
      }
    }
  }

  async function undo(key: string): Promise<Undone> {
    let intent = await read(() => store.get(key))
    if (intent === null) return { kind: 'unknown' }
    if (intent.state === 'local') {
      // Il servizio non sa che sia mai esistita e il lease e' ancora solo
      // riservato: non c'e' niente da ritirare, perche' non c'e' niente di
      // scritto.
      await write(() => store.remove(key))
      await announce()
      return { kind: 'removed' }
    }
    if (intent.state === 'decision_pending') {
      // Registrata ma senza risposta all'applicazione: si porta a termine
      // adesso. E' l'unico modo di sapere se c'e' qualcosa da ritirare, ed e'
      // sicuro perche' l'applicazione e' idempotente per chiave. Rispondere
      // «e' in volo» lasciava un tasto che non risponde proprio nei cinque
      // secondi in cui serve.
      try {
        await step(intent)
      } catch (error: unknown) {
        if (!isStaleWork(error) && !isRejection(error)) return { kind: 'unreachable' }
        await keep(landed(intent, error))
      }
      intent = await read(() => store.get(key))
      if (intent === null) return { kind: 'unknown' }
    }
    if (intent.state === 'rejected' || intent.state === 'stale') {
      // Non c'e' verdetto da ritirare: quello non e' mai stato scritto. Dirlo
      // e' l'unico modo perche' l'operatore non resti convinto di aver appena
      // annullato qualcosa.
      return { kind: 'nothing_written', intent }
    }
    if (intent.state !== 'applied') return { kind: 'unreachable' }
    if (intent.undoDeadline === null || now() >= Date.parse(intent.undoDeadline)) {
      return { kind: 'too_late' }
    }
    return {
      kind: 'reversal',
      intent: await add({
        sessionId: intent.sessionId,
        detectionId: intent.detectionId,
        // Il verdetto che si sta ritirando ha consumato il lease: un ritiro non
        // poggia su un gettone ma sulla finestra dei cinque secondi.
        leaseToken: null,
        reviewEpoch: intent.reviewEpoch,
        // La versione *dopo* il verdetto: e' lo stato su cui il ritiro agisce.
        expectedCandidateVersion: intent.candidateVersion ?? intent.expectedCandidateVersion + 1,
        kind: 'reversal',
        payload: {},
        originalKey: key,
      }),
    }
  }

  /**
   * Prende in carico, o dice di non averlo fatto.
   *
   * Chi chiama fa avanzare la schermata su questa promessa. Se il limite e'
   * pieno o il deposito non scrive, la decisione non esiste in nessun posto —
   * ne' sul servizio, ne' nel deposito, ne' sullo schermo — e l'unico modo
   * perche' non si perda e' che il fallimento arrivi a chi ha deciso.
   */
  async function add(seed: Seed): Promise<ReviewIntent> {
    const queue = await waiting()
    if (queue.length >= MAX_UNSENT) {
      throw new NotTaken(
        'outbox_full',
        `Raggiunto il limite di ${MAX_UNSENT} decisioni non consegnate:` +
          ' rimettiti in linea prima di continuare.',
      )
    }
    const intent: ReviewIntent = {
      idempotencyKey: newKey(),
      sessionId: seed.sessionId,
      detectionId: seed.detectionId,
      photoId: seed.photoId ?? null,
      elementId: seed.elementId ?? null,
      leaseToken: seed.leaseToken,
      reviewEpoch: seed.reviewEpoch,
      expectedCandidateVersion: seed.expectedCandidateVersion,
      kind: seed.kind,
      payload: seed.payload,
      state: 'local',
      createdAt: stamp(),
      undoDeadline: null,
      origin: 'session',
      originalKey: seed.originalKey ?? null,
      attempts: 0,
    }
    try {
      await write(() => store.put(intent))
    } catch {
      throw new NotTaken(
        'storage_unavailable',
        "Il verdetto non e' stato messo al sicuro: il deposito ha rifiutato la scrittura.",
      )
    }
    await announce()
    return intent
  }

  async function attention(): Promise<ReviewIntent[]> {
    return (await read(() => store.all())).filter((one) => OPEN.includes(one.state))
  }

  async function release(detectionIds: string[], exceptSession?: string): Promise<string[]> {
    const wanted = new Set(detectionIds)
    const released = new Set<string>()
    // **Tutte le righe, non l'ultima parola.** Un rifiuto vecchio puo' avere
    // dietro di se' un verdetto applicato sullo stesso candidato — e allora
    // `latest()` non lo vede piu' — ma all'apertura della pagina `tell()` lo
    // racconta lo stesso, perche' passa su ogni riga aperta. E' cosi' che un
    // avviso di ore prima e' rimasto in cima a una sessione nuova, il
    // 2.09.2026, sopra un candidato gia' deciso e ripreso.
    for (const intent of await read(() => store.all())) {
      const detectionId = detectionIn(subjectOf(intent))
      if (detectionId === null || !wanted.has(detectionId)) continue
      if (intent.state !== 'rejected' && intent.state !== 'stale') continue
      // Un rifiuto della sessione in corso resta: e' l'avviso vivo su cio' che
      // si sta facendo adesso. Si toglie solo quello di una sessione passata.
      if (exceptSession !== undefined && intent.sessionId === exceptSession) continue
      await write(() => store.remove(intent.idempotencyKey))
      told.delete(intent.idempotencyKey)
      released.add(detectionId)
    }
    return [...released]
  }

  async function latest(): Promise<Map<string, ReviewIntent>> {
    const found = new Map<string, ReviewIntent>()
    // In ordine di nascita: l'ultima scritta su un soggetto e' quella che
    // conta, e un ritiro nasce dopo il verdetto che ritira.
    for (const one of (await read(() => store.all())).sort((left, right) =>
      left.createdAt.localeCompare(right.createdAt),
    )) {
      found.set(subjectOf(one), one)
    }
    return found
  }

  /**
   * L'ultima decisione presa, cercata nel deposito.
   *
   * Non in memoria: una mappa candidato→chiave muore col ricaricamento della
   * pagina, e con lei la possibilita' di ritirare un verdetto che e' ancora
   * dentro i cinque secondi. I verdetti gia' ritirati non contano: un secondo
   * `U` scriverebbe un secondo ritiro sullo stesso verdetto.
   *
   * Senza candidato si guarda l'intera sessione, ma **solo** la sessione: le
   * righe della vecchia strada diretta e quelle adottate dal deposito
   * precedente nascono con l'istante dell'adozione, quindi risulterebbero le
   * piu' recenti di tutte, e il tasto punterebbe a un lavoro che nessuno puo'
   * ritirare.
   */
  async function undoLatest(sessionId: string, detectionId: string | null): Promise<Undone> {
    const rows = (await read(() => store.all())).filter((one) =>
      detectionId === null
        ? one.sessionId === sessionId
        : one.sessionId === sessionId && one.detectionId === detectionId,
    )
    const reversed = new Set(
      rows.filter((one) => one.kind === 'reversal').map((one) => one.originalKey),
    )
    const verdicts = rows
      .filter((one) => one.kind === 'verdict' && !reversed.has(one.idempotencyKey))
      .sort((left, right) => left.createdAt.localeCompare(right.createdAt))
    const last = verdicts[verdicts.length - 1]
    if (!last) return { kind: 'unknown' }
    return undo(last.idempotencyKey)
  }

  return {
    async ready(): Promise<ReviewIntent[]> {
      try {
        const adopted = await read(() => adopt(store, storage))
        // Cio' che aspetta una persona da prima del ricaricamento va ridetto:
        // la memoria degli avvisi muore con la pagina, l'esito no.
        for (const one of await attention()) tell(one)
        // Al rientro il deposito puo' contenere righe che il servizio ha gia'
        // scritto: partire consegnando vorrebbe dire riscrivere prima di
        // sapere.
        await askFirst()
        await announce()
        return adopted
      } catch (error: unknown) {
        if (!(error instanceof NotTaken)) throw error
        // Un deposito che non risponde non ha niente da consegnare e niente da
        // raccontare, e non deve impedire di aprire il banco. Non e' un
        // silenzio: lo dira' la prima decisione che prova a entrare, che e' il
        // momento in cui all'operatore serve saperlo.
        return []
      }
    },

    add,

    flush(): Promise<void> {
      // Chi chiama mentre una consegna e' in volo riceve *quella*, non una
      // promessa gia' risolta: altrimenti «ho consegnato» sarebbe vero mentre
      // la consegna e' ancora per strada.
      inFlight ??= askFirst()
        .then(drain)
        .then(announce)
        .catch((error: unknown) => {
          // Vale qui quanto vale all'apertura: se il deposito non si lascia
          // leggere non c'e' niente da consegnare, e chi scrive lo sapra'.
          if (!(error instanceof NotTaken)) throw error
        })
        .finally(() => {
          inFlight = null
        })
      return inFlight
    },

    reconcile,

    undo,

    undoLatest,

    waiting,

    attention,

    latest,
    release,
  }
}

/**
 * Cio' che la riconciliazione ha trovato, scritto nel deposito.
 *
 * `unknown` torna `local` e non «perduto»: il servizio non l'ha mai vista, e
 * quindi c'e' ancora tutto da consegnare. Sara' lui a dire, alla
 * registrazione, se lease, sessione ed era reggono ancora — deciderlo qui
 * sarebbe indovinare.
 */
function settled(intent: ReviewIntent, answer: ReconciledIntent): ReviewIntent {
  if (answer.status === 'unknown') return { ...intent, state: 'local' }
  const state = mapped(answer.status)
  return {
    ...intent,
    state,
    undoDeadline: answer.undo_deadline,
    reviewId: answer.review_id,
    candidateVersion: answer.candidate_version,
    rejectionCode: answer.rejection_code ?? intent.rejectionCode ?? null,
    note: said(state, answer.rejection_code, intent.kind) ?? intent.note ?? null,
  }
}

/**
 * La frase per un esito che il servizio ha dichiarato senza alzare un errore.
 *
 * Il codice da solo non e' una frase: `version_conflict` non dice a un
 * operatore che cosa deve fare del candidato che ha davanti. Resta comunque
 * scritto accanto, perche' e' cio' su cui una schermata puo' decidere.
 */
function said(
  state: ReviewIntent['state'],
  code: string | null,
  kind: string = 'verdict',
): string | null {
  if (state === 'stale') {
    return isCoverage(kind)
      ? "La sessione di verifica non e' piu' quella: la dichiarazione va rifatta."
      : "Il candidato e' tornato in coda: va ripreso e deciso di nuovo."
  }
  if (state !== 'rejected') return null
  // Il nome di cio' che non e' stato scritto, perche' «verdetto» detto di un
  // buco dichiarato manda a cercare un candidato che non c'e'.
  const what = isCoverage(kind) ? 'la dichiarazione' : 'il verdetto'
  return code
    ? `Il servizio non ha registrato ${what} (${code}).`
    : `Il servizio non ha registrato ${what}.`
}

function message(error: unknown): string {
  return error instanceof Error ? error.message : 'decisione non registrata'
}
